import { NgClass } from '@angular/common'
import {
  ChangeDetectorRef,
  Component,
  computed,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  signal,
  ViewChild
} from '@angular/core'
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { CheckboxModule } from 'primeng/checkbox'
import { ChipModule } from 'primeng/chip'
import { DialogModule } from 'primeng/dialog'
import { GalleriaModule } from 'primeng/galleria'
import { ImageModule } from 'primeng/image'
import { InputNumberModule } from 'primeng/inputnumber'
import { Message } from 'primeng/message'
import { SkeletonModule } from 'primeng/skeleton'
import { Textarea } from 'primeng/textarea'
import { ToggleButtonModule } from 'primeng/togglebutton'
import { ToggleSwitchModule } from 'primeng/toggleswitch'
import { TooltipModule } from 'primeng/tooltip'
import { tap } from 'rxjs'

import { BulkPricingDialogComponent } from 'app/_shared/components/bulk-pricing-dialog/bulk-pricing-dialog.component'
import { ButtonComponent } from 'app/_shared/components/button/button.component'
import { DropdownComponent } from 'app/_shared/components/dropdown/dropdown.component'
import { InputComponent } from 'app/_shared/components/input/input.component'
import { ProductNoteComponent } from 'app/_shared/components/product-note/product-note.component'
import { SizeGuideDialogComponent } from 'app/_shared/components/size-guide-dialog/size-guide-dialog.component'
import { CustomInput, Product } from 'app/_shared/interfaces/Product'
import {
  ProductVariant,
  ProductVariantSize,
  VariantImage
} from 'app/_shared/interfaces/ProductVariant'
import { UsdPipe } from 'app/_shared/pipes/currency.pipe'
import { NormalizeHtmlPipe } from 'app/_shared/pipes/normalizeHtml.pipe'
import { PricePipe } from 'app/_shared/pipes/price.pipe'
import { AnalyticsService } from 'app/_shared/services/analytics.service'
import { resolveUnitPrice, sortPriceRanges } from 'app/_shared/services/pricing.utils'
import { ProductService } from 'app/_shared/services/product.service'
import { ToastService } from 'app/_shared/services/toast.service'
import { AppConfigService } from 'app/app-config.service'
import { compareColors, isLightColor } from 'app/pages/product/utils/color.utils'
import { CartStore } from 'app/store/cart.store'
import { UserStore } from 'app/store/user.store'

@Component({
  selector: 'app-product',
  imports: [
    NgClass,
    InputNumberModule,
    FormsModule,
    SkeletonModule,
    ReactiveFormsModule,
    ToggleButtonModule,
    ImageModule,
    UsdPipe,
    PricePipe,
    ButtonComponent,
    GalleriaModule,
    ChipModule,
    DialogModule,
    SizeGuideDialogComponent,
    InputComponent,
    TranslatePipe,
    CheckboxModule,
    NormalizeHtmlPipe,
    ProductNoteComponent,
    DropdownComponent,
    ToggleSwitchModule,
    TooltipModule,
    BulkPricingDialogComponent,
    Message,
    Textarea
  ],
  templateUrl: './product.component.html',
  styleUrl: './product.component.scss'
})
export class ProductComponent implements OnInit {
  cartStore = inject(CartStore)
  userStore = inject(UserStore)
  fb = inject(FormBuilder)
  appConfigService: AppConfigService = inject(AppConfigService)
  productService = inject(ProductService)
  private changeDetectorRef = inject(ChangeDetectorRef)
  analyticsService = inject(AnalyticsService)

  productSlugInput = input<string>()
  maxQuantForSelection = input<number>()
  itemAddedCallback = output()

  size = signal<string>('')
  quantity = signal<number>(1)
  selectedVariant = signal<ProductVariant | undefined>(undefined)

  pricingSelectionChanged = signal(false)

  selectedVariantQuantity = computed<number | undefined>(() => {
    const size = this.size()
    const variant = this.selectedVariant()
    const max = this.maxQuantForSelection()

    // Early return if we don't have required data
    if (!size || !variant) {
      return max ?? undefined
    }

    // Find available quantity for the selected size
    const sizeQuantity = variant.product_variant_sizes.find(
      varSize => varSize.size === size
    )?.quantity

    // For kits: return a lower value between max-allowed and in-stock quantity
    if (max !== undefined) {
      return Math.min(max, sizeQuantity ?? max)
    }

    // For regular products: return available quantity for selected size
    return sizeQuantity
  })
  giftQuantityLimitReached = computed(() => {
    const max = this.maxQuantForSelection()
    return max !== undefined && (max <= 0 || this.quantity() > max)
  })

  outOfStock = computed(() => {
    if (this.selectedProduct.is_gift_card) return false
    if (this.selectedVariantQuantity() !== undefined) {
      return (
        this.selectedVariantQuantity() === 0 || this.quantity() > this.selectedVariantQuantity()!
      )
    }
    return false
  })

  belowMinimumOrderQuantity = computed(() => {
    if (!this.selectedProduct?.minimum_order_quantity) return false
    const minimum = this.selectedProduct?.minimum_order_quantity_amount ?? 0
    return minimum > 0 && this.quantity() < minimum
  })

  showInventoryLevels = computed(() => !!this.selectedProduct?.show_inventory_levels)

  loading = signal(false)
  messages: any[] = []
  private storefrontId: number
  productSlug: string
  storefrontStatus: string
  get popupPurchaseBlocked() {
    return !this.appConfigService.canCheckout
  }
  selectedProduct: Product
  galleriaImages: any[] = []
  galleriaActiveIndex: number = 0
  isMobile: boolean = false
  color: string = ''

  // Touch event properties for swipe
  private touchStartX: number = 0
  private touchEndX: number = 0
  private readonly swipeThreshold: number = 50

  liked: boolean = false
  images: string[] = []
  selectedImageIndex: number = 0

  availableSizes: string[] = []
  toastService = inject(ToastService)
  displayPrice: number = 0
  errorMessage: string = ''
  isDescriptionExpanded: boolean = false
  displayDialog: boolean = false
  protected disablePrices: boolean
  private initialVariantId: number | null = null

  giftCardEmail = new FormControl('', [Validators.email])
  giftCardRecipientName = new FormControl('')
  giftCardAmount = new FormControl('')
  giftCardNote = new FormControl('', [Validators.maxLength(500)])
  useCustomAmount = new FormControl(false) // T

  customInputsForm: FormGroup

  @ViewChild('sizeGuideDialog') sizeGuideDialog!: SizeGuideDialogComponent
  @ViewChild('bulkPricingDialog') bulkPricingDialog!: BulkPricingDialogComponent
  constructor(
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.initialVariantId = this.router.currentNavigation()?.extras.state?.['variantId'] ?? null
  }

  ngOnInit(): void {
    this.disablePrices = !!this.appConfigService.getAppConfig.disabled_prices
    this.storefrontId = this.appConfigService.getAppConfig?.id
    this.storefrontStatus = this.appConfigService.getAppConfig?.status
    // Detect initial screen size
    this.checkScreenSize()

    this.route.paramMap
      .pipe(
        tap(params => {
          const slug = this.productSlugInput() || params.get('slug')
          if (!slug) {
            this.router.navigate(['/not-found'])
            throw new Error('No slug in URL')
          }
          this.productSlug = slug
        })
      )
      .subscribe({
        next: () => {
          this.fetchSingleProduct()
        },
        error: err => {
          console.error('Route param error', err)
        }
      })

    this.giftCardAmount.valueChanges.subscribe(value => {
      if (this.useCustomAmount.value) {
        this.updateDisplayPrice()
        if (value && +value > 0) {
          this.updateAddToCartMessage()
        }
      }
    })
  }

  // Listen for window resize to update `isMobile`
  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize()
  }

  get inputFields(): FormArray {
    return this.customInputsForm.get('inputFields') as FormArray
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth < 576
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0].screenX
  }

  onTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].screenX
    this.handleSwipe()
  }

  private handleSwipe(): void {
    const swipeDistance = this.touchStartX - this.touchEndX

    if (Math.abs(swipeDistance) > this.swipeThreshold) {
      if (swipeDistance > 0) {
        // Swiped left - go to next image
        this.goToNextImage()
      } else {
        // Swiped right - go to previous image
        this.goToPreviousImage()
      }
    }
  }

  private goToNextImage(): void {
    if (this.galleriaImages.length > 0) {
      this.galleriaActiveIndex = (this.galleriaActiveIndex + 1) % this.galleriaImages.length
    }
  }

  private goToPreviousImage(): void {
    if (this.galleriaImages.length > 0) {
      this.galleriaActiveIndex =
        this.galleriaActiveIndex === 0
          ? this.galleriaImages.length - 1
          : this.galleriaActiveIndex - 1
    }
  }

  fetchSingleProduct() {
    this.loading.set(true)
    this.productService.getProduct(this.storefrontId, this.productSlug).subscribe({
      next: response => {
        this.selectedProduct = {
          ...response.data,
          product_variants:
            (response.data.product_variants ?? []).filter(
              v => !v.catalog_product_variant?.supplier_product_variant?.discontinued
            ) ?? []
        }
        // sort the variants immediately after fetch
        if (this.selectedProduct.product_variants && !this.selectedProduct.is_custom) {
          this.selectedProduct.product_variants.sort((a, b) => compareColors(a.color, b.color))
        }

        if (
          this.selectedProduct.minimum_order_quantity &&
          this.selectedProduct.minimum_order_quantity_amount
        ) {
          this.quantity.set(this.selectedProduct.minimum_order_quantity_amount)
        }

        this.loading.set(false)
        if (!this.selectedProduct.is_custom) {
          this.availableSizes = this.getAllAvailableSizes()
        }
        this.autoSelectInitialVariant()

        this.parseDescription()

        this.galleriaImages = this.images.map((imgUrl, index) => ({
          itemImageSrc: imgUrl + `?t=${Date.now()}`,
          thumbnailImageSrc: imgUrl + `?t=${Date.now()}`,
          alt: `Image ${index + 1}`,
          title: `Image ${index + 1}`
        }))

        this.createCustomInputsForm(this.selectedProduct.input_fields ?? [])

        // Only show library/approval warnings on fetch
        this.updateAddToCartMessage(true)

        this.analyticsService.trackViewItem(this.selectedProduct)
      },
      error: err => {
        this.loading.set(false)

        this.errorMessage =
          err.status === 404
            ? 'The requested product was not found!'
            : err.status === 500
              ? 'Server error. Please try again later.'
              : err.error?.message || 'Failed to load product details.'

        this.toastService.error(this.errorMessage)
        this.router.navigate(['/not-found'], {
          queryParams: { product: err.status === 404 }
        })
      }
    })
  }

  autoSelectInitialVariant() {
    if (this.selectedProduct.product_variants && this.selectedProduct.product_variants.length > 0) {
      const preselected = this.initialVariantId
        ? this.selectedProduct.product_variants.find(v => v.id === this.initialVariantId)
        : null

      if (preselected) {
        this.selectVariant(preselected, false)
      } else {
        const firstVariant = this.selectedProduct.product_variants[0] as ProductVariant
        const thumbnailVariant = this.selectedProduct.product_variants.find(
          variant => variant.id === this.selectedProduct.product_thumbnail?.fileable_id
        )
        this.selectVariant(thumbnailVariant ?? firstVariant, false)
      }
    }
  }

  createCustomInputsForm(inputs: CustomInput[]) {
    const items = inputs.map(input => {
      return this.fb.group({
        id: input.id,
        label: input.label,
        type: input.type,
        placeholder: input.placeholder ?? '',
        is_required: input.is_required,
        dropdownOptions: [input.dropdownOptions ?? []],
        onLabel: input.onLabel ?? 'On',
        offLabel: input.offLabel ?? 'Off',
        value: [
          input.type === 'toggle' ? false : '',
          input.is_required ? [Validators.required] : []
        ]
      })
    })
    this.customInputsForm = this.fb.group({
      inputFields: this.fb.array(items)
    })
  }

  updateDisplayPrice(): void {
    const bulkPricingEnabled = this.isBulkPricingEnabled
    if (this.selectedProduct.is_gift_card) {
      const useCustom = this.useCustomAmount.value

      if (useCustom) {
        const customAmount = this.giftCardAmount.value
        this.displayPrice = customAmount && +customAmount > 0 ? +customAmount * 100 : 0
      } else {
        this.displayPrice = resolveUnitPrice(
          this.currentSizeObj,
          this.quantity(),
          bulkPricingEnabled,
          this.selectedProduct.final_price
        )
      }
      return
    }

    this.displayPrice = resolveUnitPrice(
      this.currentSizeObj,
      this.quantity(),
      bulkPricingEnabled,
      this.selectedProduct.final_price
    )
  }

  onCustomAmountToggle(): void {
    const useCustom = this.useCustomAmount.value

    if (useCustom) {
      this.giftCardAmount.setValidators([Validators.required, Validators.min(1)])
      this.size.set('')
    } else {
      this.giftCardAmount.setValue(null)
      this.giftCardAmount.clearValidators()
      this.autoSelectInitialVariant()
    }

    this.giftCardAmount.updateValueAndValidity()
    this.updateDisplayPrice()
  }

  isValidEmail(email: string): boolean {
    if (!email) return false
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  cartItemPlaceholder() {
    return (
      (this.selectedProduct?.is_gift_card && 'assets/images/giftcard.png') ||
      (this.selectedProduct?.is_custom && 'assets/images/custom-product.png') ||
      'assets/demo/images/product/t-shirt-placeholder.png'
    )
  }

  addItem(): void {
    if (this.popupPurchaseBlocked) {
      return
    }
    if (this.customInputsForm.invalid) {
      this.customInputsForm.markAllAsTouched()
      return
    }
    if (
      (!this.useCustomAmount?.value && (!this.selectedVariant() || !this.size())) ||
      (this.useCustomAmount?.value && !this.giftCardAmount?.value)
    ) {
      this.updateAddToCartMessage()
      // this.toastService.error('Please select a color and size')
      return
    }

    if (this.belowMinimumOrderQuantity()) {
      this.updateAddToCartMessage()
      return
    }

    let selectedSizeObj: any

    if (!this.useCustomAmount?.value) {
      selectedSizeObj = this.selectedVariant()?.product_variant_sizes.find(
        s => s.size === this.size()
      )
      if (!selectedSizeObj) {
        this.toastService.error('Selected size is not available')
        return
      }
    }

    // Decide which image to store in the cart
    let imageUrl
    if (this.selectedVariant()?.variant_images && this.selectedVariant()?.variant_images?.length) {
      // Attempt to find "frontImage"
      const frontImage = this.selectedVariant()?.variant_images?.find(
        img => img.type === 'frontImage'
      )
      // If frontImage found, use that; otherwise use the first variant image
      imageUrl = frontImage
        ? frontImage.full_path
        : this.selectedVariant()?.variant_images?.[0]?.full_path
    } else if (this.selectedProduct.is_gift_card && this.selectedProduct.product_thumbnail) {
      imageUrl = this.selectedProduct.product_thumbnail?.full_path
    }

    if (!imageUrl) {
      imageUrl = this.cartItemPlaceholder()
    }

    const formatedCustomAmount = +(this.giftCardAmount?.value || 0) * 100
    const bulkPricingEnabled = this.isBulkPricingEnabled

    const finalPrice = this.useCustomAmount?.value
      ? formatedCustomAmount
      : resolveUnitPrice(
          selectedSizeObj,
          this.quantity(),
          bulkPricingEnabled,
          this.selectedProduct.final_price
        )

    const cartItem: any = {
      name: this.selectedProduct.name,
      id: this.selectedProduct.id,
      product_id: this.selectedProduct.id,
      quantity: this.quantity(),
      size: this.size() || this.giftCardAmount?.value,
      product_thumbnail: this.selectedProduct.product_thumbnail,
      price: finalPrice,
      final_price: finalPrice,
      product_size_id: selectedSizeObj?.id || null,
      product_color_id: this.selectedProduct.product_color_id,
      product_variant_id: this.selectedVariant()?.id,
      color: this.selectedVariant()?.color,
      image: imageUrl,
      weight: selectedSizeObj?.weight || 0,
      warehouse: this.selectedProduct.warehouse,
      slug: this.selectedProduct.slug,
      input_fields: this.customInputsForm.value.inputFields,
      handling_fee: this.selectedProduct.handling_fee,
      handling_fee_additional_item: this.selectedProduct.handling_fee_additional_item,
      price_ranges: selectedSizeObj?.price_ranges ?? [],
      bulk_pricing_enabled: bulkPricingEnabled,
      minimum_order_quantity: this.selectedProduct.minimum_order_quantity,
      minimum_order_quantity_amount: this.selectedProduct.minimum_order_quantity_amount
    }

    if (this.selectedProduct.is_gift_card) {
      cartItem.email = this.giftCardEmail.value
      cartItem.recipient_name = this.giftCardRecipientName.value
      cartItem.is_gift_card = this.selectedProduct.is_gift_card
    }
    if (this.giftCardNote?.value?.length) {
      cartItem.note = this.giftCardNote?.value
    }
    this.cartStore.addCartItem(cartItem)
    this.analyticsService.trackAddToCart(cartItem, this.quantity())
    if (this.cartStore.giftingToken()) {
      this.itemAddedCallback.emit()
    } else {
      this.displayDialog = true
    }
  }

  continueShopping(): void {
    this.displayDialog = false
    this.quantity.set(this.minQuantity)
  }

  goToCheckout(): void {
    this.displayDialog = false
    this.quantity.set(this.minQuantity)
    this.router.navigate([!this.cartStore.giftingToken() ? '/cart' : '/checkout'])
  }

  selectVariant(variant: ProductVariant, isUserAction: boolean = true): void {
    if (isUserAction) {
      this.pricingSelectionChanged.set(true)
    }

    this.selectedVariant.set(variant)

    if (this.selectedProduct.is_custom) {
      this.availableSizes = variant.product_variant_sizes?.map(s => s.size) ?? []
    }

    // Keep the previously selected size if the new variant has it,
    const variantAvailableSizesArray = this.getVariantAvailableSizes(variant)
    const previousSize = this.size()
    const keepPreviousSize = !!previousSize && variantAvailableSizesArray.includes(previousSize)
    this.size.set(keepPreviousSize ? previousSize : (variantAvailableSizesArray[0] ?? ''))

    // All variant images
    const allVariantImages: VariantImage[] = variant?.variant_images ?? []

    // Find front and rear if they exist
    const front = allVariantImages.find(img => img.type === 'frontImage')
    const rear = allVariantImages.find(img => img.type === 'rearImage')

    // Everything else (not front/rear)
    const others = allVariantImages.filter(
      img => img.type !== 'frontImage' && img.type !== 'rearImage'
    )

    const orderedImages: VariantImage[] = []

    if (front) orderedImages.push(front)
    if (rear) orderedImages.push(rear)
    orderedImages.push(...others)
    const additionalPhotos = (this.selectedProduct.additional_photos ?? []).filter(img => {
      const variantIds = img.catalog_product_variant_ids ?? []
      return (
        !variantIds?.length ||
        (variant.catalog_product_variant.id !== null &&
          variantIds.includes(variant.catalog_product_variant.id))
      )
    })
    orderedImages.push(...additionalPhotos)

    this.images = orderedImages.map(img => img.full_path + `?t=${Date.now()}`)
    if (this.selectedProduct.is_gift_card) {
      this.images.push(this.selectedProduct.product_thumbnail?.full_path ?? '')
    }
    this.selectedImageIndex = 0

    const nextGalleriaImages = this.images.map((imgUrl, index) => ({
      itemImageSrc: imgUrl + `?t=${Date.now()}`,
      thumbnailImageSrc: imgUrl + `?t=${Date.now()}`,
      alt: `Variant Image ${index + 1}`,
      title: `Variant Image ${index + 1}`
    }))

    this.galleriaActiveIndex = 0

    if (isUserAction) {
      // Reset only Galleria's bound items; keep the p-galleria instance mounted.
      this.galleriaImages = []
      this.changeDetectorRef.detectChanges()
    }

    this.galleriaImages = nextGalleriaImages

    this.updateDisplayPrice()
    this.updateAddToCartMessage()
  }

  isSizeDisabled(sizeOption: string): boolean {
    if (!this.selectedVariant() || this.useCustomAmount?.value) {
      // No variant selected; all sizes are disabled
      return true
    } else {
      // Disable sizes not available in the selected variant
      return !this.selectedVariant()?.product_variant_sizes.some(
        sizeObj => sizeObj.size === sizeOption
      )
    }
  }

  selectSize(sizeOption: string) {
    if (!this.isSizeDisabled(sizeOption)) {
      this.pricingSelectionChanged.set(true)
      this.size.set(sizeOption)
      this.updateDisplayPrice()
      this.updateAddToCartMessage()
    }
  }

  getQuantityForSize(sizeOption: string): number | null {
    const sizeObj = this.selectedVariant()?.product_variant_sizes.find(s => s.size === sizeOption)
    return sizeObj ? sizeObj.quantity : null
  }

  selectImageIndex(i: number): void {
    this.selectedImageIndex = i
  }

  sortSizes(arr: string[]): string[] {
    const sizeOrder = [
      'XXS',
      'XS',
      'S',
      'M',
      'L',
      'XL',
      '2XL',
      'XXL',
      '3XL',
      'XXXL',
      '4XL',
      'XXXXL',
      '5XL',
      '6XL'
    ]
    return arr.sort((a, b) => sizeOrder.indexOf(a) - sizeOrder.indexOf(b))
  }

  getVariantAvailableSizes(variant): string[] {
    const sizesSet = new Set<string>()
    if (variant.product_variant_sizes) {
      variant.product_variant_sizes.forEach(sizeObj => {
        sizesSet.add(sizeObj.size)
      })
    }
    return Array.from(sizesSet)
  }

  getAllAvailableSizes(): string[] {
    const sizesSet = new Set<string>()
    if (this.selectedProduct && this.selectedProduct.product_variants) {
      this.selectedProduct.product_variants.forEach(variant => {
        if (variant.product_variant_sizes) {
          variant.product_variant_sizes.forEach(sizeObj => {
            sizesSet.add(sizeObj.size)
          })
        }
      })
    }
    const sizes = Array.from(sizesSet)
    return this.selectedProduct.is_custom ? sizes : this.sortSizes(sizes)
  }

  get isArrayDescription(): boolean {
    return Array.isArray(this.selectedProduct?.description)
  }

  private parseDescription(): void {
    const rawDesc = this.selectedProduct?.description
    if (rawDesc && rawDesc.trim().startsWith('[')) {
      try {
        this.selectedProduct.description = JSON.parse(rawDesc)
      } catch (error) {
        console.warn('Description is not valid JSON array:', error)
      }
    }
  }

  updateAddToCartMessage(onlyApprovalWarnings: boolean = false): void {
    this.messages = []

    if (this.storefrontStatus === 'live' && !this.selectedProduct?.library_approved) {
      this.messages.push({
        severity: 'warn',
        detail: 'This product is not available due to library approval!'
      })
    }
    if (this.storefrontStatus === 'live' && this.selectedProduct?.need_attention !== 0) {
      this.messages.push({
        severity: 'warn',
        detail: 'This product is not approved for sale!'
      })
    }
    if (!onlyApprovalWarnings) {
      if (!this.selectedVariant() && !this.useCustomAmount?.value) {
        this.messages.push({
          severity: 'warn',
          detail: 'Please select a color!'
        })
      }
      if (!this.size() && !this.useCustomAmount?.value) {
        this.messages.push({
          severity: 'warn',
          detail: !this.selectedProduct?.is_gift_card
            ? 'Please select a size!'
            : 'Please select amount'
        })
      }
      if (this.selectedProduct?.is_gift_card && this.useCustomAmount?.value) {
        if (!this.giftCardAmount.value || +this.giftCardAmount.value <= 0) {
          this.messages.push({
            severity: 'warn',
            detail: 'Please enter a valid gift card amount!'
          })
        }
      }
      if (this.belowMinimumOrderQuantity()) {
        this.messages.push({
          severity: 'warn',
          detail: `Minimum order quantity for this product is ${this.selectedProduct?.minimum_order_quantity_amount}.`
        })
      }
    }
  }

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded
  }

  private truncateText(text: string, wordLimit: number): string {
    const plainText = text.replaceAll('&nbsp;', ' ')
    const words = plainText.split(/\s+/)
    if (words.length <= wordLimit) {
      return text
    }
    return words.slice(0, wordLimit).join(' ') + '...'
  }

  get displayedDescription(): string[] | string {
    if (!this.selectedProduct?.description) {
      return ''
    }

    if (this.isArrayDescription) {
      const descArray = this.selectedProduct.description
      return this.isDescriptionExpanded ? descArray : descArray.slice(0, 4)
    } else {
      const descString = this.selectedProduct.description as string
      if (this.isDescriptionExpanded) {
        return descString.replaceAll('&nbsp;', ' ')
      }
      // Return truncated version if not expanded
      return this.truncateText(descString, 25)
    }
  }

  shouldShowToggleButton(): boolean {
    if (!this.selectedProduct?.description) {
      return false
    }

    if (this.isArrayDescription) {
      const descArray = this.selectedProduct.description
      return descArray.length > 4
    } else {
      const plainText = this.selectedProduct.description.replaceAll('&nbsp;', ' ')
      const words = plainText.split(/\s+/)
      return words.length > 25
    }
  }

  getControl(i: number, name: string): FormControl {
    return this.inputFields.controls[i].get(name) as FormControl
  }

  protected readonly isLightColor = isLightColor

  get currentSizeObj(): ProductVariantSize | undefined {
    return this.selectedVariant()?.product_variant_sizes.find(s => s.size === this.size())
  }

  get currentPriceRanges() {
    return this.currentSizeObj?.price_ranges ?? []
  }

  get isBulkPricingEnabled(): boolean {
    if (this.selectedProduct?.is_custom) {
      return !!this.selectedVariant()?.bulk_pricing_enabled
    }
    return !!this.selectedProduct?.bulk_pricing_enabled
  }

  get minQuantity(): number {
    if (
      this.selectedProduct?.minimum_order_quantity &&
      this.selectedProduct?.minimum_order_quantity_amount
    ) {
      return this.selectedProduct.minimum_order_quantity_amount
    }
    return 1
  }

  openBulkPricingDialog(): void {
    this.bulkPricingDialog.open()
  }

  onQuantityChange(value: number) {
    this.pricingSelectionChanged.set(true)
    this.quantity.set(value)
    this.updateDisplayPrice()
  }

  get bulkDiscountSummary(): { maxSavingsPercent: number; minQuantityForMax: number } | null {
    if (!this.isBulkPricingEnabled || !this.currentPriceRanges.length) return null

    const sorted = sortPriceRanges(this.currentPriceRanges)
    const basePrice = this.currentSizeObj?.price ?? 0
    const bestTier = sorted[sorted.length - 1]

    if (basePrice <= 0 || !bestTier) return null

    const maxSavingsPercent = Math.round(((basePrice - bestTier.price) / basePrice) * 100)
    if (maxSavingsPercent <= 0) return null

    return {
      maxSavingsPercent,
      minQuantityForMax: bestTier.min_quantity
    }
  }
}
